import { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store";
import { fetchUserPermissions } from "../store/slices/authSlice";
import { Permission } from "../types/auth.types";

export const usePermissions = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { permissions, username, isLoading, error } = useSelector(
    (state: RootState) => state.auth
  );

  useEffect(() => {
    if (username && permissions.length === 0) {
      dispatch(fetchUserPermissions());
    }
  }, [dispatch, username, permissions.length]);

  const permissionCache = new Map<string, Permission>();
  permissions.forEach((permission) => {
    permissionCache.set(permission.name, permission);
    permissionCache.set(permission.url, permission);
    permissionCache.set(permission.route, permission);
  });

  return {
    permissions,
    isLoading,
    error,
    hasPermission: (keyOrRouteOrUrl: string) =>
      permissionCache.has(keyOrRouteOrUrl),
    hasAnyPermission: (keysOrRoutesOrUrls: string[]) =>
      keysOrRoutesOrUrls.some((k) => permissionCache.has(k)),
    hasAllPermissions: (keysOrRoutesOrUrls: string[]) =>
      keysOrRoutesOrUrls.every((k) => permissionCache.has(k)),
    getPermissionByKey: (key: string) => permissionCache.get(key),
    getPermissionsByKeys: (keys: string[]) =>
      keys.map((k) => permissionCache.get(k)).filter(Boolean) as Permission[],
    getPermissionByRoute: (route: string) => permissionCache.get(route),
    getPermissionsByRoutes: (routes: string[]) =>
      routes.map((r) => permissionCache.get(r)).filter(Boolean) as Permission[],
    getPermissionByUrl: (url: string) => permissionCache.get(url),
    getPermissionsByUrls: (urls: string[]) =>
      urls.map((u) => permissionCache.get(u)).filter(Boolean) as Permission[],
  };
};
